import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from '../../shared/components/Icons';
import { FlowBand } from '../../shared/booking/FlowParts';
import { fmtDay, formatPrice } from '../../shared/data/common';
import { offerDetail } from '../../api/SupplierResults';
import { reviewFlight } from '../../api/flights';
import { useAuth } from '../../auth/AuthContext';
import { AirlineLogo } from './FlightSummary';
import { loadSelection, withFare } from './liveSelection';

// How many of a flight's other fares are checked for add-ons when the chosen fare has none.
const MAX_FARE_PROBES = 6;

const ruleTypes = [
  ['CANCELLATION', 'Cancellation'],
  ['DATECHANGE', 'Date change'],
  ['NO_SHOW', 'No show'],
  ['SEAT_CHARGEABLE', 'Seat selection'],
];

const formatDuration = (mins) => `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
const timeOf = (iso) => (iso || '').slice(11, 16);
const dayOf = (iso) => (iso ? new Date(iso.slice(0, 10) + 'T00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) : '');

function referencesOf(offer) {
  const reference = offerDetail(offer, 'offerReference');
  if (!reference) return [];
  try {
    const parsed = JSON.parse(reference);
    return Array.isArray(parsed) ? parsed : [reference];
  } catch {
    return [reference];
  }
}

/** Reads TripJack's review response into display-ready trips, fare and rules. */
function parseReview(review) {
  const data = review?.data || review || {};
  const trips = (data.tripInfos || []).flatMap((trip) => {
    const segments = (trip.sI || []).map((s) => ({
      isReturn: s.isRs === true,
      id: s.id,
      airline: { code: s.fD?.aI?.code || '', name: s.fD?.aI?.name || s.fD?.aI?.code || 'Airline', color: '#3867d6' },
      flightNo: `${s.fD?.aI?.code || ''}-${s.fD?.fN || ''}`,
      aircraft: s.fD?.eT || '',
      from: { code: s.da?.code, city: s.da?.city || s.da?.code, name: s.da?.name, terminal: s.da?.terminal },
      to: { code: s.aa?.code, city: s.aa?.city || s.aa?.code, name: s.aa?.name, terminal: s.aa?.terminal },
      dep: s.dt,
      arr: s.at,
      duration: Number(s.duration) || 0,
      layoverAfter: Number(s.cT) || 0,
      meals: (s.ssrInfo?.MEAL || []).length,
      // On connecting legs TripJack omits the amount: baggage bought on the first leg covers the journey.
      baggage: (s.ssrInfo?.BAGGAGE || []).filter((o) => o.amount != null).length,
    }));
    const fare = trip.totalPriceList?.[0] || {};
    // An international return is one trip holding both directions; return segments carry isRs.
    const parts = [segments.filter((s) => !s.isReturn), segments.filter((s) => s.isReturn)].filter((p) => p.length);
    return parts.map((part) => ({
      segments: part,
      fare,
      adult: fare.fd?.ADULT || {},
      journeyMins: part.reduce((n, s) => n + s.duration + s.layoverAfter, 0),
    }));
  });
  return {
    trips,
    bookingId: data.bookingId,
    total: data.totalPriceInfo?.totalFareDetail?.fC || {},
    sessionSeconds: Number(data.conditions?.st) || 0,
    seatsApplicable: data.conditions?.isa === true,
  };
}

/** Which paid extras the supplier offers for a reviewed fare. */
function addOnsOf(parsed) {
  const segments = parsed.trips.flatMap((t) => t.segments);
  return {
    seats: parsed.seatsApplicable,
    meals: segments.some((s) => s.meals > 0),
    baggage: segments.some((s) => s.baggage > 0),
  };
}

const hasAddOns = (addOns) => addOns.seats || addOns.meals || addOns.baggage;

function AddOnBadges({ addOns }) {
  return (
    <span className="lfd-addon-badges">
      {[['seats', 'Seat selection'], ['meals', 'Meals'], ['baggage', 'Extra baggage']].map(([k, label]) => (
        <span key={k} className={`lfd-addon ${addOns[k] ? 'yes' : 'no'}`}>
          <Icon name={addOns[k] ? 'check' : 'close'} size={12} /> {label}
        </span>
      ))}
    </span>
  );
}

/** Other fares on the same flight that do include seats, meals or baggage. */
function FareAlternatives({ state, onSwitch }) {
  if (state.loading) {
    return (
      <section className="panel lfd-alternatives" role="status">
        <h3><Icon name="search" size={18} /> Looking for fares with seats, meals or baggage…</h3>
        <p className="muted small">Checking this flight's other fares with the airline.</p>
      </section>
    );
  }
  if (!state.checked) return null;
  return (
    <section className="panel lfd-alternatives">
      <h3><Icon name="tag" size={18} /> Want to choose seats, meals or extra baggage?</h3>
      {state.list.length ? (
        <>
          <p className="muted small">This fare doesn't include them. These fares on the same flight do:</p>
          <div className="lfd-alt-list">
            {state.list.map(({ fare, addOns }) => (
              <div key={fare.ref} className="lfd-alt">
                <div>
                  <b>{fare.name}</b>
                  <AddOnBadges addOns={addOns} />
                </div>
                <b className="price">{formatPrice(fare.price)}</b>
                <button type="button" className="btn btn-outline" onClick={() => onSwitch(fare)}>Switch to this fare</button>
              </div>
            ))}
          </div>
        </>
      ) : (
        <p className="muted small">None of this flight's fares include seat selection, meals or extra baggage in advance. Seats are assigned free at check-in.</p>
      )}
    </section>
  );
}

function ruleWindow(rule) {
  const st = Number(rule.st);
  const et = Number(rule.et);
  if (!st && (!et || et >= 8760)) return 'Any time';
  if (et >= 8760) return `More than ${st} hours before departure`;
  if (!st) return `Within ${et} hours of departure`;
  return `${st}–${et} hours before departure`;
}

function ruleCharge(rule) {
  const amount = Number(rule.amount || 0) + Number(rule.additionalFee || 0);
  return rule.amount != null ? formatPrice(amount) : '—';
}

function TripPanel({ trip, label, cabin }) {
  const first = trip.segments[0];
  const last = trip.segments[trip.segments.length - 1];
  if (!first) return null;
  const stops = trip.segments.length - 1;
  return (
    <section className="panel">
      <div className="sum-head">
        <div className="airline">
          <AirlineLogo airline={first.airline} />
          <div>
            <h3>{label}{first.from.city} → {last.to.city}</h3>
            <span className="muted small">
              {fmtDay(first.dep.slice(0, 10))} · {stops ? `${stops} stop${stops > 1 ? 's' : ''}` : 'Non-stop'} · {formatDuration(trip.journeyMins)} · {cabin}
            </span>
          </div>
        </div>
        {trip.fare.fareIdentifier && <span className="tag">{trip.fare.fareIdentifier.replace(/_/g, ' ')}</span>}
      </div>
      <div className="timeline lfd-timeline">
        {trip.segments.map((s, i) => (
          <div key={s.id || i}>
            <div className="tl-seg">
              <div className="tl-airline">
                <AirlineLogo airline={s.airline} size={28} />
                <span className="small"><b>{s.airline.name}</b><br /><span className="muted">{s.flightNo}{s.aircraft ? ` · ${s.aircraft}` : ''}</span></span>
              </div>
              <div className="tl-point">
                <b className="time">{timeOf(s.dep)}</b>
                <span className="small"><b>{s.from.code}</b> · {s.from.city}</span>
                <span className="muted small">{s.from.name}{s.from.terminal ? `, ${s.from.terminal}` : ''}</span>
                <span className="muted small">{dayOf(s.dep)}</span>
              </div>
              <div className="tl-dur">
                <span className="muted small">{formatDuration(s.duration)}</span>
                <span className="path-line"><span className="plane"><Icon name="flights" size={13} /></span></span>
              </div>
              <div className="tl-point">
                <b className="time">{timeOf(s.arr)}</b>
                <span className="small"><b>{s.to.code}</b> · {s.to.city}</span>
                <span className="muted small">{s.to.name}{s.to.terminal ? `, ${s.to.terminal}` : ''}</span>
                <span className="muted small">{dayOf(s.arr)}</span>
              </div>
            </div>
            {i < trip.segments.length - 1 && (
              <div className="layover"><Icon name="clock" size={14} /> {formatDuration(s.layoverAfter)} layover in {s.to.city}</div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function FareDetails({ trip, addOns }) {
  const a = trip.adult;
  const refund = { 0: 'Non-refundable', 1: 'Refundable', 2: 'Partially refundable' }[a.rT];
  const rules = trip.fare.fareRuleInformation?.tfr || {};
  const ruleRows = ruleTypes.filter(([key]) => rules[key]?.length);
  return (
    <>
      <section className="panel">
        <h3><Icon name="bag" size={18} /> Baggage & fare details</h3>
        <table className="data-table">
          <tbody>
            <tr><th>Cabin baggage</th><td>{a.bI?.cB || 'As per airline'} per adult</td></tr>
            <tr><th>Check-in baggage</th><td>{a.bI?.iB || 'As per airline'} per adult</td></tr>
            <tr><th>Meal</th><td>{a.mI ? 'Complimentary meal included' : 'Meals available to purchase'}</td></tr>
            {refund && <tr><th>Refundability</th><td>{refund}</td></tr>}
            <tr><th>Fare class</th><td>{[a.cc, a.cB && `Class ${a.cB}`, a.fB && `Fare basis ${a.fB}`].filter(Boolean).join(' · ') || '—'}</td></tr>
            {a.sR != null && <tr><th>Seats left at this fare</th><td>{a.sR}</td></tr>}
            <tr><th>Add-ons with this fare</th><td><AddOnBadges addOns={addOns} /></td></tr>
          </tbody>
        </table>
      </section>
      <section className="panel">
        <h3><Icon name="file" size={18} /> Cancellation & date change</h3>
        {ruleRows.length ? (
          <table className="data-table lfd-rules">
            <thead><tr><th>Rule</th><th>When</th><th>Airline fee</th><th>Policy</th></tr></thead>
            <tbody>
              {ruleRows.flatMap(([key, name]) => rules[key].map((rule, i) => (
                <tr key={`${key}-${i}`}>
                  <td><b>{i === 0 ? name : ''}</b></td>
                  <td>{ruleWindow(rule)}</td>
                  <td>{ruleCharge(rule)}</td>
                  <td className="muted small">{(rule.policyInfo || '').split('__nls__').map((line) => <div key={line}>{line}</div>)}</td>
                </tr>
              )))}
            </tbody>
          </table>
        ) : <p className="muted">The airline did not provide fare rules for this fare.</p>}
        <p className="muted small">Fees are per passenger and set by the airline; they may change until the ticket is issued. A ₹299 Trawtel fee applies to cancellations.</p>
      </section>
    </>
  );
}

function useCountdown(seconds, startedAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!seconds) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [seconds, startedAt]);
  return seconds ? Math.max(0, Math.round(seconds - (now - startedAt) / 1000)) : null;
}

export default function LiveFlightDetails() {
  const { selectionId } = useParams();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const selection = useMemo(() => loadSelection(selectionId), [selectionId]);
  const [attempt, setAttempt] = useState(0);
  const [flight, setFlight] = useState(() => selection?.flight);
  const [alternatives, setAlternatives] = useState({ loading: false, checked: false, list: [] });
  const [result, setResult] = useState({ loading: true, review: null, error: null, at: 0 });
  // Review is open to guests; a signed-in customer's token is sent when present.
  const token = auth?.accessToken;

  useEffect(() => {
    if (!flight) return undefined;
    let cancelled = false;
    setResult({ loading: true, review: null, error: null, at: 0 });
    reviewFlight(flight.offer.supplierCode, referencesOf(flight.offer), token)
      .then((review) => !cancelled && setResult({ loading: false, review, error: null, at: Date.now() }))
      .catch((error) => !cancelled && setResult({ loading: false, review: null, error, at: 0 }));
    return () => { cancelled = true; };
  }, [flight, token, attempt]);

  const parsed = useMemo(() => (result.review ? parseReview(result.review) : null), [result.review]);
  const addOns = parsed ? addOnsOf(parsed) : null;
  const secondsLeft = useCountdown(parsed?.sessionSeconds, result.at);
  const needAlternatives = Boolean(addOns && !hasAddOns(addOns));
  const currentRef = flight ? offerDetail(flight.offer, 'offerReference') : '';
  const supplierCode = flight?.offer.supplierCode;
  const otherFares = useMemo(
    () => (selection?.allFares || []).filter((fare) => fare.ref && fare.ref !== currentRef).slice(0, MAX_FARE_PROBES),
    [selection, currentRef],
  );

  // When the chosen fare has no add-ons, review the flight's other fares to find ones that do.
  useEffect(() => {
    if (!needAlternatives || !otherFares.length) {
      setAlternatives({ loading: false, checked: needAlternatives, list: [] });
      return undefined;
    }
    let cancelled = false;
    setAlternatives({ loading: true, checked: false, list: [] });
    Promise.all(otherFares.map((fare) => reviewFlight(supplierCode, [fare.ref], token)
      .then((review) => ({ fare, addOns: addOnsOf(parseReview(review)) }))
      .catch(() => null)))
      .then((results) => {
        if (cancelled) return;
        const list = results.filter((r) => r && hasAddOns(r.addOns)).sort((a, b) => a.fare.price - b.fare.price);
        setAlternatives({ loading: false, checked: true, list });
      });
    return () => { cancelled = true; };
  }, [needAlternatives, otherFares, supplierCode, token]);

  const switchFare = (fare) => {
    setAlternatives({ loading: false, checked: false, list: [] });
    setFlight((current) => withFare(current, fare));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!selection) {
    return (
      <div className="container section empty">
        <span className="empty-icon"><Icon name="clock" size={34} /></span>
        <h2>This flight selection has expired</h2>
        <p>Fares change often. Search again to see the latest prices.</p>
        <Link to={`/flights/results${search}`} className="btn btn-primary">Back to flights</Link>
      </div>
    );
  }

  const { query } = selection;
  const pax = {
    adults: Number(query.adults || 1),
    children: Number(query.children || 0),
    infants: Number(query.infants || 0),
  };
  const paxTotal = pax.adults + pax.children + pax.infants;
  const cabin = query.cabin || 'Economy';
  const back = { to: `/flights/results${selection.search || search}`, label: 'Back to flights' };
  const title = `${flight.fromCity} → ${flight.toCity}`;

  const total = Number(parsed?.total.TF) || flight.price;
  const priceChanged = parsed && Math.round(total) !== Math.round(flight.price);
  const expired = secondsLeft === 0;

  const proceed = () => {
    const trips = parsed.trips;
    // Trips after the first fly the way back, both for domestic (two trips) and international returns.
    const allSegments = trips.flatMap((t, ti) => t.segments.map((s, si) => ({ ...s, isReturn: ti > 0, startsTrip: si === 0 })));
    const firstTrip = trips[0];
    const returnTrip = trips[1];
    const summary = {
      from: flight.fromCity,
      to: flight.toCity,
      date: firstTrip?.segments[0]?.dep?.slice(0, 10) || query.date,
      returnDate: returnTrip?.segments[0]?.dep?.slice(0, 10) || query.returnDate || undefined,
      pax,
      airline: flight.airline,
      flightNo: flight.flightNo,
      fareName: firstTrip?.fare.fareIdentifier?.replace(/_/g, ' ') || 'Supplier',
      cabin,
      stops: flight.stops,
      duration: firstTrip ? formatDuration(firstTrip.journeyMins) : flight.duration,
      baggage: {
        cabin: firstTrip?.adult.bI?.cB || flight.cabinBag || 'As per airline',
        checkin: firstTrip?.adult.bI?.iB || flight.checkinBag || 'As per airline',
      },
      segments: allSegments.map((s, i) => ({
        from: s.from.city,
        to: s.to.city,
        fromCode: s.from.code,
        toCode: s.to.code,
        fromAirport: s.from.name,
        toAirport: s.to.name,
        date: s.dep?.slice(0, 10),
        isReturn: s.isReturn,
        dep: timeOf(s.dep),
        arr: timeOf(s.arr),
        duration: formatDuration(s.duration),
        flightNo: s.flightNo,
        // No layover between the onward arrival and the return departure.
        layover: i > 0 && !s.startsTrip && allSegments[i - 1].layoverAfter ? formatDuration(allSegments[i - 1].layoverAfter) : undefined,
      })),
    };
    navigate('/flights/review', {
      state: {
        summary,
        backTo: pathname + search,
        liveOffer: {
          supplierCode: flight.offer.supplierCode,
          totalPrice: total,
          currency: flight.offer.currency || 'INR',
          offerReferences: referencesOf(flight.offer),
          review: result.review,
          details: flight.offer.details || {},
        },
      },
    });
  };

  return (
    <>
      <FlowBand title={title} step={1} back={back} />
      <div className="container details-layout">
        <div className="details-main">
          {result.loading && (
            <section className="panel lfd-loading" role="status">
              <span className="sk sk-logo" />
              <div><b>Checking the latest fare with {flight.airline.name}…</b><span className="muted small">This usually takes a few seconds.</span></div>
            </section>
          )}
          {result.error && (
            <div className="api-message error" role="alert">
              <b>We couldn't confirm this fare.</b> {result.error.message}
              <div className="lfd-actions">
                <button type="button" className="btn btn-primary" onClick={() => setAttempt(attempt + 1)}>Try again</button>
                <Link to={back.to} className="btn">Choose another flight</Link>
              </div>
            </div>
          )}
          {parsed && parsed.trips.map((trip, i) => (
            <TripPanel key={i} trip={trip} cabin={cabin} label={parsed.trips.length > 1 ? (i === 0 ? 'Onward · ' : 'Return · ') : ''} />
          ))}
          {needAlternatives && <FareAlternatives state={alternatives} onSwitch={switchFare} />}
          {parsed?.trips[0] && <FareDetails trip={parsed.trips[0]} addOns={addOns} />}
        </div>

        <aside className="details-side">
          <div className="panel price-card">
            {flight.fareName && <span className="tag lfd-fare-name">{flight.fareName} fare</span>}
            <span className="muted small">Total for {paxTotal} traveller{paxTotal > 1 ? 's' : ''}</span>
            <b className="price big">{formatPrice(total)}</b>
            {priceChanged && (
              <div className="api-message warning lfd-note">The fare changed from {formatPrice(flight.price)} when you searched.</div>
            )}
            {parsed && (
              <div className="fare-breakdown">
                {parsed.total.BF != null && <div className="summary-line"><span>Base fare</span><span>{formatPrice(parsed.total.BF)}</span></div>}
                {parsed.total.TAF != null && <div className="summary-line"><span>Taxes & airline fees</span><span>{formatPrice(parsed.total.TAF)}</span></div>}
                <div className="summary-line total"><span>Total</span><b>{formatPrice(total)}</b></div>
              </div>
            )}
            {secondsLeft != null && (
              <div className={`lfd-timer ${secondsLeft < 120 ? 'urgent' : ''}`}>
                <Icon name="clock" size={14} />
                {expired
                  ? 'Fare session expired'
                  : `Fare held for ${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`}
              </div>
            )}
            {expired ? (
              <button type="button" className="btn btn-primary block lg" onClick={() => setAttempt(attempt + 1)}>Refresh fare</button>
            ) : (
              <button type="button" className="btn btn-primary block lg" disabled={!parsed} onClick={proceed}>Continue</button>
            )}
            <span className="muted small">Traveller details on the next step. No payment is taken yet.</span>
          </div>
        </aside>
      </div>
    </>
  );
}
